import React, { useState } from "react";
import clientAxiosInstance from "../../../api/clientAxiosInstance";

const SUBMITTABLE_STATUSES = [
  "shared_with_client",
  "shared_with_client_onboarding_done",
  "shared_with_client_for_final_review",
];

const SubmitPanel = ({ household_id, clientToken, householdStatus, hasUnsavedEdits, onSubmitSuccess }) => {
  const [noteValue, setNoteValue] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const canSubmit = SUBMITTABLE_STATUSES.includes(householdStatus);

  const handleSubmit = async () => {
    if (hasUnsavedEdits || !household_id || !clientToken || !canSubmit) return;

    setSubmitting(true);
    setError("");
    setSuccess("");

    try {
      const response = await clientAxiosInstance.post(
        `/client/household/${household_id}/submit`,
        { client_note: noteValue || null },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${clientToken}`,
          },
        },
      );

      if (response.data && response.data.success === true) {
        setSuccess(response.data.message || "Submitted successfully!");
        setSubmitted(true);
        if (onSubmitSuccess) onSubmitSuccess();
      } else {
        setError("Failed to submit. Please try again.");
      }
    } catch (err) {
      const errorMessage =
        err.response?.data?.detail || err.response?.data?.error || "Failed to submit. Please try again.";
      setError(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
      <h3 className="text-lg font-bold text-gray-900 mb-2">Ready to submit?</h3>
      <p className="text-sm text-gray-500 mb-5">
        You can return this to your advisor even if some items are incomplete. They can help finalize the details.
      </p>

      {/* Optional Note */}
      <div className="mb-5">
        <label className="text-sm font-semibold text-gray-700 block mb-1.5">Optional Note</label>
        <textarea
          value={noteValue}
          onChange={(e) => {
            const filtered = e.target.value.replace(/[@#$%*^]/g, "");
            if (filtered.length <= 200) setNoteValue(filtered);
          }}
          placeholder="Add a note for your advisor (e.g. what's missing or questions you have)"
          rows={4}
          maxLength={200}
          disabled={submitted}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 resize-none disabled:bg-gray-50 disabled:text-gray-400"
        />
        <p className="text-xs text-gray-400 mt-1 text-right">{noteValue.length}/200</p>
      </div>

      {/* Error / Success */}
      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-red-700 text-sm">{error}</p>
        </div>
      )}
      {success && (
        <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
          <p className="text-green-700 text-sm">{success}</p>
        </div>
      )}

      {/* Submit Button */}
      <div className="relative group">
        <button
          onClick={handleSubmit}
          disabled={!canSubmit || hasUnsavedEdits || submitting || submitted}
          className="w-full px-4 py-3 text-sm font-semibold text-white bg-emerald-500 rounded-lg hover:bg-emerald-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        >
          {submitting ? (
            <>
              <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
              Submitting...
            </>
          ) : submitted ? (
            <>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              Submitted
            </>
          ) : (
            <>
              Submit to Advisor
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
              </svg>
            </>
          )}
        </button>
        {!submitted && (hasUnsavedEdits || !canSubmit) && (
          <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-1.5 bg-gray-800 text-white text-xs rounded-lg whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
            {!canSubmit ? "Submission is not available right now." : "Save or cancel your edits first"}
            <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-800" />
          </div>
        )}
      </div>

      {/* Security footer */}
      <p className="text-xs text-gray-400 mt-4 flex items-center gap-1.5">
        <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
        </svg>
        Your data is encrypted and securely transmitted.
      </p>
    </div>
  );
};

export default SubmitPanel;
